"use client";

import dynamic from "next/dynamic";
import { useEffect, useRef, useState, type ReactNode } from "react";

import type { AppThemeTone } from "@/components/editorial/product-bridge/app-surface";

import { ReplayControls, type ReplayState } from "./replay-controls";
import { useReplayClock } from "./use-replay-clock";
import { WORKFLOW_RUN, workflowRunFrameAt } from "./workflow-run";
import { ShotWorkflowWindow } from "./workflow-window";
import type { ShotWorkspace } from "./workspace";

/**
 * The canvas, as the app loads it (WorkflowPanel): in the browser only, in
 * its own chunk, so React Flow reaches a page only with a workflow shot on
 * screen and measures its steps where they are drawn.
 */
const ShotWorkflowCanvas = dynamic(() => import("@/components/product-shot/workflow-canvas"), { ssr: false });

type PlayerProps = { workspace: ShotWorkspace; tone: AppThemeTone };

/**
 * The sample run's player: the workflow in its pane at each frame of the
 * run, on the replays' clock (it pauses off screen and on request, and
 * replays from the start). It mounts ahead of the run, so the canvas is
 * drawn before the run starts, and holds the first frame until `armed`.
 * With reduced motion it rests on the last frame, the Results tab, until
 * asked to play.
 */
function WorkflowRunPlayer({
  workspace,
  tone,
  reduceMotion,
  armed,
  onArm,
}: PlayerProps & { reduceMotion: boolean; armed: boolean; onArm: () => void }) {
  const { ref, atMs, playing, ended, toggle } = useReplayClock(WORKFLOW_RUN.endsMs, reduceMotion, armed);
  const frame = workflowRunFrameAt(atMs);
  const state: ReplayState = !armed ? "idle" : ended ? "ended" : playing ? "playing" : "paused";
  return (
    <div ref={ref}>
      <div className={state === "paused" && atMs > 0 ? "[&_*]:[animation-play-state:paused]" : undefined}>
        <ShotWorkflowWindow
          tone={tone}
          workspace={workspace}
          frame={frame}
          canvas={(topRight) => (
            <ShotWorkflowCanvas definition={frame.workflow.definition} run={frame.run} reducedMotion={reduceMotion} {...topRight} />
          )}
          inert
          aria-hidden="true"
        />
      </div>
      <ReplayControls state={state} progress={atMs / WORKFLOW_RUN.endsMs} onToggle={armed ? toggle : onArm} />
    </div>
  );
}

/**
 * Holds the run's first frame, rendered on the server, until the player
 * takes over: the player mounts as the frame nears the viewport, so the
 * canvas loads and draws out of sight, and the run starts once a third of
 * the frame is in view. With reduced motion the player mounts at once,
 * resting on the run's last frame.
 */
export function WorkflowRunIsland({ children, ...props }: PlayerProps & { children: ReactNode }) {
  const islandRef = useRef<HTMLDivElement>(null);
  const [player, setPlayer] = useState<{ reduceMotion: boolean } | null>(null);
  const [armed, setArmed] = useState(false);

  useEffect(() => {
    const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const island = islandRef.current;
    if (reduceMotion || !island || typeof IntersectionObserver === "undefined") {
      setPlayer({ reduceMotion });
      setArmed(true);
      return;
    }
    const near = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        near.disconnect();
        setPlayer((current) => current ?? { reduceMotion });
      },
      { rootMargin: "200px 0px" },
    );
    const inView = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        inView.disconnect();
        setPlayer((current) => current ?? { reduceMotion });
        setArmed(true);
      },
      { threshold: 0.35 },
    );
    near.observe(island);
    inView.observe(island);
    return () => {
      near.disconnect();
      inView.disconnect();
    };
  }, []);

  return (
    <div ref={islandRef}>
      {player ? (
        <WorkflowRunPlayer {...props} reduceMotion={player.reduceMotion} armed={armed} onArm={() => setArmed(true)} />
      ) : (
        children
      )}
    </div>
  );
}
